import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

import { Button } from '@domcrm/embed-ui';

export type ConfirmOptions = {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Опасное действие (удаление): кнопка подтверждения красная. */
  danger?: boolean;
};

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmDialogContext = createContext<ConfirmFn | null>(null);

type PendingConfirm = ConfirmOptions & { resolve: (ok: boolean) => void };

/**
 * Подтверждение действий в стиле приложения вместо системного `window.confirm`
 * (в мобильных приложениях системное окно выглядит чужеродно и показывает адрес сайта).
 */
export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...options, resolve });
      }),
    []
  );

  const close = useCallback(
    (ok: boolean) => {
      setPending((current) => {
        current?.resolve(ok);
        return null;
      });
    },
    []
  );

  useEffect(() => {
    if (!pending) return;
    confirmButtonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending, close]);

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      {pending ? (
        <div
          className="fixed inset-0 z-[120] flex items-end justify-center bg-black/55 p-4 sm:items-center"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close(false);
          }}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-[var(--bg-elevated)] p-5 shadow-lg"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="embed-confirm-title"
            aria-describedby={pending.message ? 'embed-confirm-desc' : undefined}
          >
            <h2 id="embed-confirm-title" className="text-lg font-semibold text-[var(--text)]">
              {pending.title}
            </h2>
            {pending.message ? (
              <p id="embed-confirm-desc" className="mt-2 text-sm text-[var(--text-muted)]">
                {pending.message}
              </p>
            ) : null}
            <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse sm:justify-start">
              <Button
                ref={confirmButtonRef}
                type="button"
                variant={pending.danger ? 'danger' : 'primary'}
                className="w-full sm:w-auto"
                onClick={() => close(true)}
              >
                {pending.confirmLabel ?? 'Подтвердить'}
              </Button>
              <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={() => close(false)}>
                {pending.cancelLabel ?? 'Отмена'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </ConfirmDialogContext.Provider>
  );
}

/** Функция подтверждения; вне провайдера — запасной вариант через системное окно. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmDialogContext);
  return ctx ?? ((o) => Promise.resolve(window.confirm(o.message ? `${o.title}\n\n${o.message}` : o.title)));
}
